import { useCallback } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import type { Path, UseFormSetValue } from 'react-hook-form'

import { getGetCustomerSummaryApiV1CustomersCustomerIdSummaryGetQueryOptions } from '#/shared/api/generated/shipment/theCargoShipment'
import type { CustomerSummary } from '#/shared/api/generated/shipment/models'

import type { LocationFormData } from './location-schema'

type LocationVariant = 'origin' | 'destination'
type LocationField = 'businessName' | 'contact'

export type SameAsCustomerHandler = (
  variant: LocationVariant,
  field: LocationField,
) => Promise<void> | void

const BUSINESS_SUMMARY_MAP: ReadonlyArray<
  [keyof LocationFormData['origin'], keyof CustomerSummary]
> = [
  ['address', 'address'],
  ['city', 'city'],
  ['state', 'state'],
  ['zip', 'zip'],
  ['phone', 'phone'],
  ['mobile', 'secondary_phone'],
  ['additionalNote', 'note'],
  ['type', 'company_type'],
]

const CONTACT_SUMMARY_MAP: ReadonlyArray<
  [keyof LocationFormData['origin'], keyof CustomerSummary]
> = [
  ['phone', 'phone'],
  ['mobile', 'secondary_phone'],
  ['additionalNote', 'note'],
]

function customerFullName(summary: CustomerSummary): string {
  return [summary.first_name, summary.last_name].filter(Boolean).join(' ')
}

function applySummaryFields(
  variant: LocationVariant,
  summary: CustomerSummary,
  setValue: UseFormSetValue<LocationFormData>,
  map: ReadonlyArray<[keyof LocationFormData['origin'], keyof CustomerSummary]>,
) {
  const setOpts = { shouldValidate: true, shouldDirty: true } as const
  for (const [target, source] of map) {
    const value = summary[source]
    if (typeof value === 'string' && value)
      setValue(`${variant}.${target}` as Path<LocationFormData>, value, setOpts)
  }
}

function applyBusinessSummary(
  variant: LocationVariant,
  summary: CustomerSummary,
  setValue: UseFormSetValue<LocationFormData>,
) {
  const setOpts = { shouldValidate: true, shouldDirty: true } as const
  const contact = customerFullName(summary)
  const businessName = summary.company ?? contact
  if (businessName)
    setValue(
      `${variant}.businessName` as Path<LocationFormData>,
      businessName,
      setOpts,
    )
  if (contact)
    setValue(`${variant}.contact` as Path<LocationFormData>, contact, setOpts)
  applySummaryFields(variant, summary, setValue, BUSINESS_SUMMARY_MAP)
}

function applyContactSummary(
  variant: LocationVariant,
  summary: CustomerSummary,
  setValue: UseFormSetValue<LocationFormData>,
) {
  const setOpts = { shouldValidate: true, shouldDirty: true } as const
  const contact = customerFullName(summary)
  if (contact)
    setValue(`${variant}.contact` as Path<LocationFormData>, contact, setOpts)
  applySummaryFields(variant, summary, setValue, CONTACT_SUMMARY_MAP)
}

export function useSameAsCustomer(
  customerId: string | null | undefined,
  setValue: UseFormSetValue<LocationFormData>,
): SameAsCustomerHandler | undefined {
  const queryClient = useQueryClient()
  const handler = useCallback<SameAsCustomerHandler>(
    async (variant, field) => {
      if (!customerId) return
      const response = await queryClient.fetchQuery(
        getGetCustomerSummaryApiV1CustomersCustomerIdSummaryGetQueryOptions(
          customerId,
        ),
      )
      if (field === 'businessName')
        applyBusinessSummary(variant, response.data, setValue)
      else applyContactSummary(variant, response.data, setValue)
    },
    [customerId, queryClient, setValue],
  )
  return customerId ? handler : undefined
}
